import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE, useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';
import { formatCount, formatDate } from '../../lib/admin-utils';
import type { ImportJob } from '../../types/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { RefreshCw, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import ImportComposerDialog from './ImportComposerDialog';
import { ListRowsSkeleton } from './skeletons';
import {
  JOBS_PER_PAGE,
  isValidationBusy,
  jobStatusBadgeVariant,
  parseApiErrorPayload,
  progressLabel,
  type ImportJobsResponse,
} from './import-utils';

export default function ImportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedTenant, selectedTenantId, domains, canWriteLinks, setNotice } = useAdmin();

  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsCursor, setJobsCursor] = useState<string | null>(null);
  const [jobsNextCursor, setJobsNextCursor] = useState<string | null>(null);
  const [jobsCursorHistory, setJobsCursorHistory] = useState<Array<string | null>>([]);
  const [jobsPage, setJobsPage] = useState(1);
  const [composerOpen, setComposerOpen] = useState(false);

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const fetchJobs = useCallback(async (cursor: string | null): Promise<ImportJobsResponse> => {
    if (!selectedTenantId) {
      setJobs([]);
      return { jobs: [], nextCursor: null, hasMore: false };
    }

    const query = new URLSearchParams();
    query.set('tenantId', selectedTenantId);
    query.set('limit', String(JOBS_PER_PAGE));
    if (cursor) {
      query.set('cursor', cursor);
    }

    const response = await fetch(`${API_BASE}/import-jobs?${query.toString()}`, {
      headers: authHeaders,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw parseApiErrorPayload(data, t('imports.errorFetch'));
    }

    const data = await response.json();
    return {
      jobs: Array.isArray(data.jobs) ? data.jobs : [],
      nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
      hasMore: Boolean(data.hasMore),
    };
  }, [authHeaders, selectedTenantId, t]);

  const loadJobsPage = useCallback(async (cursor: string | null, page: number) => {
    setJobsLoading(true);
    try {
      const data = await fetchJobs(cursor);
      setJobs(data.jobs);
      setJobsCursor(cursor);
      setJobsNextCursor(data.hasMore ? data.nextCursor : null);
      setJobsPage(page);
      return data;
    } finally {
      setJobsLoading(false);
    }
  }, [fetchJobs]);

  useEffect(() => {
    if (!selectedTenantId) {
      setJobs([]);
      setJobsCursor(null);
      setJobsNextCursor(null);
      setJobsCursorHistory([]);
      setJobsPage(1);
      return;
    }

    loadJobsPage(null, 1).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : t('imports.errorFetch');
      setNotice({ type: 'error', message });
    });
  }, [loadJobsPage, selectedTenantId, setNotice, t]);

  // Auto-refresh when active jobs exist
  useEffect(() => {
    if (!selectedTenantId) return;

    const hasActiveJobs = jobs.some((job) => job.status === 'running' || isValidationBusy(job));
    const timer = window.setInterval(() => {
      fetchJobs(jobsCursor)
        .then((data) => {
          setJobs(data.jobs);
          setJobsNextCursor(data.hasMore ? data.nextCursor : null);
        })
        .catch(() => undefined);
    }, hasActiveJobs ? 2500 : 9000);

    return () => window.clearInterval(timer);
  }, [fetchJobs, jobs, jobsCursor, selectedTenantId]);

  const goToPreviousJobsPage = async () => {
    if (jobsCursorHistory.length === 0) return;
    const history = [...jobsCursorHistory];
    const previousCursor = history.pop() ?? null;
    setJobsCursorHistory(history);
    await loadJobsPage(previousCursor, Math.max(1, jobsPage - 1));
  };

  const goToNextJobsPage = async () => {
    if (!jobsNextCursor) return;
    setJobsCursorHistory((current) => [...current, jobsCursor]);
    await loadJobsPage(jobsNextCursor, jobsPage + 1);
  };

  if (!selectedTenant) {
    return (
      <Card className="py-12 text-center">
        <CardContent>
          <h3 className="text-lg font-semibold">{t('common.noWorkspace')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('imports.noWorkspaceDesc')}</p>
        </CardContent>
      </Card>
    );
  }

  const runningCount = jobs.filter((job) => job.status === 'running').length;
  const reviewCount = jobs.filter((job) => job.status === 'review').length;
  const failedCount = jobs.filter((job) => job.status === 'failed').length;
  const cancelledCount = jobs.filter((job) => job.status === 'cancelled').length;
  const hasPrev = jobsCursorHistory.length > 0;
  const hasNext = Boolean(jobsNextCursor);

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>{t('imports.cardTitle')}</CardTitle>
              <CardDescription>{t('imports.cardDesc')}</CardDescription>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadJobsPage(jobsCursor, jobsPage).catch(() => undefined)}
                disabled={jobsLoading}
              >
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', jobsLoading && 'animate-spin')} />
                {t('imports.refresh')}
              </Button>
              <Button size="sm" onClick={() => setComposerOpen(true)} disabled={!canWriteLinks}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t('imports.newImport')}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Compact status strip */}
          {jobs.length > 0 && (runningCount > 0 || reviewCount > 0 || failedCount > 0 || cancelledCount > 0) && (
            <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
              {runningCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                  {t('imports.statusRunning', { count: runningCount })}
                </span>
              )}
              {reviewCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-yellow-500" />
                  {t('imports.statusReview', { count: reviewCount })}
                </span>
              )}
              {failedCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-destructive" />
                  {t('imports.statusFailed', { count: failedCount })}
                </span>
              )}
              {cancelledCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-slate-400" />
                  {t('imports.statusCancelled', { count: cancelledCount })}
                </span>
              )}
            </div>
          )}

          {/* Job list */}
          {jobsLoading && jobs.length === 0 ? (
            <ListRowsSkeleton rows={5} />
          ) : jobs.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-semibold">{t('imports.emptyTitle')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('imports.emptyDesc')}</p>
            </div>
          ) : (
            <div className="divide-y rounded-lg border">
              {jobs.map((job) => (
                <Link
                  key={job.jobId}
                  to={`/imports/${job.jobId}`}
                  className="flex flex-col gap-3 px-3 py-3 transition-colors hover:bg-accent/40 sm:flex-row sm:items-center sm:gap-4 sm:px-4"
                >
                  {/* Left: info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {job.fileName || `${job.sourceType.toUpperCase()} import`}
                      </p>
                      <Badge variant={jobStatusBadgeVariant(job.status)} className="shrink-0 text-[10px]">
                        {job.status}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:gap-3">
                      <span>{formatDate(job.createdAt)}</span>
                      <span className="hidden sm:inline">{job.createdBy}</span>
                      {job.domain && <span>{t('imports.jobDomain', { domain: job.domain })}</span>}
                      <span>{t('imports.jobSuccessCount', { n: formatCount(job.successCount) })}</span>
                      {job.failedCount > 0 && (
                        <span className="text-destructive">{t('imports.jobErrorCount', { n: formatCount(job.failedCount) })}</span>
                      )}
                    </div>
                  </div>

                  {/* Right: progress */}
                  <div className="hidden sm:flex items-center gap-3 shrink-0 w-40">
                    <Progress value={job.progress} className="flex-1 h-1.5" />
                    <span className="text-xs text-muted-foreground w-10 text-right">{progressLabel(job)}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:hidden">
                    <Progress value={job.progress} className="h-1.5 flex-1" />
                    <span className="w-10 text-right text-xs text-muted-foreground">{progressLabel(job)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {(hasPrev || hasNext) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{t('common.page', { n: jobsPage })}</p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => goToPreviousJobsPage().catch(() => undefined)}
                  disabled={!hasPrev || jobsLoading}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => goToNextJobsPage().catch(() => undefined)}
                  disabled={!hasNext || jobsLoading}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ImportComposerDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        selectedTenant={selectedTenant}
        selectedTenantId={selectedTenantId}
        domains={domains}
        canWriteLinks={canWriteLinks}
        token={token}
        setNotice={setNotice}
        onCreated={async (job) => {
          setJobsCursorHistory([]);
          await loadJobsPage(null, 1);
          navigate(`/imports/${job.jobId}`);
        }}
      />
    </div>
  );
}
